<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center py-4">
      <v-progress-circular indeterminate color="blue darken-1" size="28" width="2" />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="caption red--text px-1">
      {{ error }}
    </div>

    <!-- No offers -->
    <div v-else-if="offers.length === 0" class="caption grey--text px-1 py-2">
      Aucune offre associée à ce compte.
    </div>

    <!-- Offers list -->
    <template v-else>
      <div v-for="offer in offers" :key="offer.slug" class="mb-4">
        <!-- Offer header -->
        <div class="caption grey--text text--darken-1 px-1 mb-2">
          Offre souscrite : <v-chip color="gray" small class="ml-2 mb-2 font-weight-medium">{{ offer.name }}</v-chip>
        </div>

        <v-card class="settings-card rounded-lg" outlined>
          <v-card-text class="pa-0">
            <div
              v-for="(limit, index) in offer.limits"
              :key="limit.name"
              class="limit-row px-4 py-3"
              :class="{ 'limit-row--bordered': index > 0 }"
            >
                <!-- Header row -->
                <div class="d-flex align-center mb-1">
                  <v-icon small :color="statusColor(limit)" class="mr-2">
                    {{ statusIcon(limit) }}
                  </v-icon>
                  <span class="subtitle-2 font-weight-medium flex-grow-1">
                    Quota {{ periodLabel(limit.period) }}
                  </span>
                  <span
                    class="caption font-weight-medium"
                    :class="limit.exceeded ? 'red--text text--darken-1' : 'grey--text text--darken-1'"
                  >
                    <template v-if="isAdmin">
                      {{ formatCost(limit.current_cost) }} / {{ formatCost(limit.cost_cap) }}
                    </template>
                    <template v-else>
                      {{ limit.exceeded ? '100 %' : `${limit.percentage} %` }}
                    </template>
                  </span>
                </div>

                <!-- Period badge + description -->
                <div class="d-flex align-center mb-2">
                  <span v-if="limit.description" class="caption grey--text">{{ limit.description }}</span>
                </div>

                <!-- Animated progress bar -->
                <v-progress-linear
                  :value="animatedValue(offer.slug, index)"
                  :color="barColor(limit)"
                  background-color="grey lighten-3"
                  rounded
                  height="6"
                />

                <!-- Percentage label -->
                <div class="d-flex justify-space-between mt-1">
                  <span class="caption grey--text">
                    {{ limit.exceeded ? 'Plafond atteint' : `${limit.percentage} % utilisé` }}
                  </span>
                  <span v-if="offer.expires_at" class="caption grey--text">
                    Expire le {{ formatDate(offer.expires_at) }}
                  </span>
                </div>
              </div>
          </v-card-text>
        </v-card>
      </div>
    </template>
  </div>
</template>

<script>
import { fetchOffer } from '@/services/limitsApi'

const PERIOD_LABELS = {
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuel',
  total: 'Total',
}

export default {
  name: 'UserLimits',

  computed: {
    isAdmin() {
      return this.$store.getters['settings/isStaff'] || this.$store.getters['settings/isSuperuser']
    },
  },

  data() {
    return {
      offers: [],
      animated: {},
      loading: true,
      error: null,
    }
  },

  async mounted() {
    await this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.offers = await fetchOffer()
        // Initialise all bars at 0
        const zero = {}
        this.offers.forEach(offer => {
          zero[offer.slug] = offer.limits.map(() => 0)
        })
        this.animated = zero
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            const filled = {}
            this.offers.forEach(offer => {
              filled[offer.slug] = offer.limits.map(l => l.percentage)
            })
            this.animated = filled
          })
        })
      } catch (err) {
        this.error = err.message || 'Impossible de charger les limites.'
      } finally {
        this.loading = false
      }
    },

    animatedValue(slug, index) {
      return (this.animated[slug] || [])[index] || 0
    },

    periodLabel(period) {
      return PERIOD_LABELS[period] || period
    },

    formatCost(value) {
      const n = parseFloat(value)
      if (isNaN(n)) return value
      return `${n.toFixed(4)} $`
    },

    formatDate(iso) {
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },

    statusColor(limit) {
      if (limit.exceeded) return 'red darken-1'
      if (limit.percentage >= 90) return 'orange darken-1'
      return 'green darken-1'
    },

    statusIcon(limit) {
      if (limit.exceeded) return 'mdi-alert-circle-outline'
      if (limit.percentage >= 90) return 'mdi-alert-outline'
      return 'mdi-check-circle-outline'
    },

    barColor(limit) {
      if (limit.exceeded) return 'red darken-1'
      if (limit.percentage >= 90) return 'orange darken-1'
      if (limit.percentage >= 70) return 'amber darken-1'
      return 'blue darken-1'
    },
  },
}
</script>

<style scoped>
.settings-card {
  border-color: #e0e4ea !important;
  overflow: hidden;
}

.limit-row {
  transition: background-color 0.15s;
}

.limit-row--bordered {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
